/**
 * OptInDialog — convite para o Programa de recomendação (UI enxuta de 17/10/2026). Mostra o
 * desconto de quem a pessoa indicar e o que ela ganha, pede o final do link (conferido no backend
 * enquanto digita) e o aceite do regulamento.
 * Abre depois de uma nota 9 ou 10 na avaliação (motivo "survey") e em /referral para quem ainda
 * não participa. POST /app/ambassador { slug } → { success, slug } | { error }. Ao ativar, vai
 * para /referral.
 *
 * Movimento (23/10/2026): véu e diálogo (.ds-veu, .ds-dialogo) entram com .is-aberto; ao fechar,
 * saem em 140 ms e só então avisam o AmbassadorHost (que desmonta). Enquanto o final do link é
 * conferido, o texto "Conferindo…" fica embaixo do campo; disponível, o ✓ aparece no lugar
 * (.ds-troca); a mensagem embaixo abre espaço (.ds-altura) em vez de saltar. A adesão solta o
 * confete (celebrar, decisão D15 com M7 b), que não aparece com "reduzir movimento". Enquanto
 * sai, o diálogo continua montado e com o foco: Enter no campo não ativa de novo.
 */
import { useCallback, useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { useNavigate } from 'react-router-dom';
import { useBrand } from '../../hooks/useBrand';
import { usePresenca } from '../../hooks/usePresenca';
import { useJoinAmbassador } from '../../api/useAmbassador';
import { checkSlug } from '../../api/useReferral';
import { useSessionStore } from '../../stores/sessionStore';
import { emitActivity } from '../../lib/activity';
import { showConfetti } from '../../lib/celebrate';
import { semMovimento, tokenMs } from '../../lib/movimento';
import { referralLink, suggestSlug } from '../referral/referralShare';
import { RulesDialog } from '../referral/ReferralDashboard';
import { useReferralText } from '../referral/referralText';
import '../referral/referral.css';

/** Percentuais padrão de quem entra (config/ambassador.json); o admin ajusta por pessoa depois. */
const DEFAULT_PERCENT = 10;
const DEFAULT_DISCOUNT = 10;
const SLUG_RE = /^[a-z0-9-]{3,30}$/;

type SlugState = 'checking' | 'available' | 'taken' | 'invalid';

interface OptInDialogProps {
  onClose: () => void;
  reason?: 'survey' | null;
}

export function OptInDialog({ onClose, reason }: OptInDialogProps): JSX.Element {
  const { tx } = useReferralText();
  const brand = useBrand();
  const navigate = useNavigate();
  const session = useSessionStore((s) => s.session);
  const join = useJoinAmbassador();
  const inputRef = useRef<HTMLInputElement | null>(null);
  const acceptRef = useRef<HTMLInputElement | null>(null);

  const [slug, setSlug] = useState(() => suggestSlug(session?.email));
  const [state, setState] = useState<SlugState>('checking');
  const [accepted, setAccepted] = useState(false);
  const [rulesOpen, setRulesOpen] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const rules = usePresenca(rulesOpen);

  // Movimento: sai em 140 ms antes de avisar quem abriu; a peça fechada não fica na tela.
  const [saindo, setSaindo] = useState(false);
  const { aberto } = usePresenca(!saindo);
  const saidaRef = useRef(0);
  useEffect(() => () => window.clearTimeout(saidaRef.current), []);
  const close = useCallback(() => {
    if (saindo) return;
    const ms = tokenMs('--duracao-140');
    if (ms <= 0) {
      onClose();
      return;
    }
    setSaindo(true);
    saidaRef.current = window.setTimeout(onClose, ms);
  }, [saindo, onClose]);

  const product = brand.appTitle;
  const prefix = referralLink('', brand.source).display;

  // O diálogo fica invisível (visibility) até ganhar .is-aberto: o foco vai ao campo depois.
  useEffect(() => {
    if (aberto) inputRef.current?.focus();
  }, [aberto, reason]);

  useEffect(() => {
    const onKey = (e: KeyboardEvent): void => {
      if (e.key === 'Escape' && !rulesOpen) close();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [close, rulesOpen]);

  // Confere o identificador no backend 350 ms depois da última tecla.
  useEffect(() => {
    if (!SLUG_RE.test(slug)) {
      setState('invalid');
      return undefined;
    }
    setState('checking');
    const controller = new AbortController();
    const timer = setTimeout(() => {
      checkSlug(slug, controller.signal)
        .then((res) => {
          if (res.slug !== slug) return;
          setState(!res.valid ? 'invalid' : res.available ? 'available' : 'taken');
        })
        .catch(() => {
          // Sem resposta: deixa ativar; o backend confere de novo ao salvar.
          if (!controller.signal.aborted) setState('available');
        });
    }, 350);
    return () => {
      clearTimeout(timer);
      controller.abort();
    };
  }, [slug]);

  const canActivate = state === 'available' && accepted && !join.isPending;

  function activate(): void {
    if (saindo) return;
    // Desativado do guia: ao clicar, diz o que falta (regulamento) ou leva ao campo do link.
    if (!canActivate) {
      if (join.isPending || state === 'checking') return;
      if (state === 'invalid' || state === 'taken') {
        inputRef.current?.focus();
        return;
      }
      if (!accepted) {
        setError(tx('accept_required'));
        acceptRef.current?.focus();
      }
      return;
    }
    setError(null);
    join.mutate(slug, {
      onSuccess: (res) => {
        if (res.error === 'slug_already_exists') return setState('taken');
        if (res.error === 'slug_invalid') return setState('invalid');
        if (res.error || !res.success) return setError(tx('join_error'));
        emitActivity('ambassador.joined');
        // Celebração (N5): sem confete com "reduzir movimento", também na opção do produto.
        if (!semMovimento()) showConfetti();
        close();
        navigate('/referral');
      },
      onError: () => setError(tx('join_error')),
    });
  }

  // Mensagem embaixo do campo: o espaço abre e fecha com .ds-altura (200 ms) e o texto troca no
  // lugar com fade (key por estado). Ao fechar, a última mensagem fica (aria-hidden) até o espaço
  // terminar de fechar, para não sumir antes da altura.
  const message: Exclude<SlugState, 'available'> | null = state === 'available' ? null : state;
  const [lastMessage, setLastMessage] = useState(message);
  if (message && message !== lastMessage) setLastMessage(message);
  const line = usePresenca(message !== null, '--duracao-200');
  const shown = line.montado ? lastMessage : null;
  const status = shown ? (
    <span
      key={shown}
      className={`rf-slug__status rf-slug__status--${shown === 'checking' ? 'wait' : 'bad'} ds-aparece`}
      aria-hidden={message ? undefined : true}
    >
      {shown === 'checking'
        ? tx('slug_checking')
        : shown === 'taken'
          ? tx('slug_taken')
          : tx('slug_invalid')}
    </span>
  ) : null;

  return createPortal(
    <div
      className={`rf-overlay ds-veu${aberto ? ' is-aberto' : ''}`}
      role="presentation"
      onClick={(e) => e.target === e.currentTarget && close()}
    >
      <section
        className={`rf-dialog rf-dialog--invite ds-dialogo${aberto ? ' is-aberto' : ''}`}
        role="dialog"
        aria-modal="true"
        aria-labelledby="rf-invite-title"
      >
        <div className="rf-dialog__head">
          <h2 id="rf-invite-title">{tx('invite_title', { product })}</h2>
          <button
            type="button"
            className="rf-dialog__close ds-resposta"
            aria-label={tx('close')}
            title={tx('close')}
            onClick={close}
          >
            <span className="material-symbols-rounded">close</span>
          </button>
        </div>

        <div className="rf-benefits">
          <div className="rf-benefit">
            <strong>{DEFAULT_DISCOUNT}%</strong>
            <span>{tx('invite_discount')}</span>
          </div>
          <div className="rf-benefit">
            <strong>{DEFAULT_PERCENT}%</strong>
            <span>{tx('invite_earn')}</span>
          </div>
        </div>

        <div className="rf-slug">
          <label htmlFor="rf-slug">{tx('slug_label')}</label>
          <div className="rf-slug__field ds-campo">
            <span
              className="rf-slug__prefix"
              aria-hidden="true"
              onClick={() => inputRef.current?.focus()}
            >
              {prefix}
            </span>
            <input
              ref={inputRef}
              id="rf-slug"
              type="text"
              autoComplete="off"
              spellCheck={false}
              maxLength={30}
              aria-describedby="rf-slug-status rf-slug-hint"
              aria-invalid={state === 'taken' || state === 'invalid'}
              value={slug}
              onChange={(e) => setSlug(e.target.value.toLowerCase().replace(/\s+/g, '-'))}
              onKeyDown={(e) => e.key === 'Enter' && activate()}
            />
            {/* Disponível: o ✓ aparece no lugar (140 ms, .ds-troca), sem mudar a largura do campo.
                Enquanto confere, só o texto "Conferindo…" embaixo diz o que acontece. */}
            <span className="ds-troca">
              {state === 'available' ? (
                <span
                  className="material-symbols-rounded rf-slug__ok"
                  role="img"
                  aria-label={tx('slug_available')}
                >
                  check
                </span>
              ) : (
                <span className="material-symbols-rounded rf-slug__ok ds-fora" aria-hidden="true">
                  check
                </span>
              )}
            </span>
          </div>
          <span className={`rf-slug__linha ds-altura${line.aberto ? ' is-aberto' : ''}`}>
            <span id="rf-slug-status" className="ds-altura-conteudo" aria-live="polite">
              {status}
            </span>
          </span>
          <span id="rf-slug-hint" className="rf-slug__hint">
            {tx('slug_hint')}
          </span>
        </div>

        <div className="rf-accept">
          <label htmlFor="rf-accept">
            <input
              ref={acceptRef}
              id="rf-accept"
              type="checkbox"
              checked={accepted}
              onChange={(e) => {
                setAccepted(e.target.checked);
                if (e.target.checked) setError(null);
              }}
            />
            {tx('accept')}
          </label>
          <button
            type="button"
            className="rf-link ds-resposta"
            onClick={() => setRulesOpen(true)}
          >
            {tx('read_rules')}
          </button>
        </div>

        {error ? (
          <div className="rf-alert" role="alert">
            <span className="material-symbols-rounded" aria-hidden="true">
              error
            </span>
            {error}
          </div>
        ) : null}

        <button
          type="button"
          className="rf-btn rf-btn--yellow rf-btn--block ds-resposta"
          aria-disabled={!canActivate || undefined}
          aria-busy={join.isPending || undefined}
          onClick={activate}
        >
          {tx('activate')}
        </button>
      </section>
      {rules.montado ? (
        <RulesDialog aberto={rules.aberto} onClose={() => setRulesOpen(false)} />
      ) : null}
    </div>,
    document.body,
  );
}
